import React, { Fragment, useEffect, useState } from 'react';
import RightSidebar from 'components/right-sidebar';
import { ReactComponent as BrandDetailsIcon } from 'assets/icons/brand-details.svg';
import { ReactComponent as OpeningTimeIcon } from 'assets/icons/opening-time.svg';
import { ReactComponent as MetadataIcon } from 'assets/icons/metadata.svg';
import { Outlet } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import BottomNavigation from 'components/bottom-navigation';
import { ROUTE_KEYS } from 'utils/constants';
import { Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { SuccessModal } from 'components/base/success-modal';
import PageHeaderText from 'components/base/page-header-text';
import { formatTimeToApiFormat, handleApiError, isFCBBrand } from 'utils/functions';
import useNewBrandContext from 'contexts/new-brand.context';
import { BrandType } from 'utils/types';

const CreateBrandContent = () => {
  const { setRightSection, activeCokitchen, category } = useDashboardContext();
  const { createBrandForm, disabled } = useNewBrandContext();
  const [newBrand, setNewBrand] = useState({} as BrandType);
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);

  const BrandDetailsNav = [
    {
      title: 'Brand details',
      link: `${ROUTE_KEYS.CREATE_BRAND}/details`,
      Icon: BrandDetailsIcon
    },
    {
      title: 'Brand images',
      link: `${ROUTE_KEYS.CREATE_BRAND}/images`,
      Icon: OpeningTimeIcon
    },
    ...(createBrandForm.preorder || !isFCBBrand(createBrandForm.brand_type)
      ? [
          {
            title: 'Brand opening time',
            link: `${ROUTE_KEYS.CREATE_BRAND}/opening-time`,
            Icon: OpeningTimeIcon
          }
        ]
      : []),
    {
      title: 'Brand Polygon',
      link: `${ROUTE_KEYS.CREATE_BRAND}/polygon`,
      Icon: BrandDetailsIcon
    },
    {
      title: 'Brand metadata',
      link: `${ROUTE_KEYS.CREATE_BRAND}/metadata`,
      Icon: MetadataIcon
    },
    {
      title: 'Brand preview',
      link: `${ROUTE_KEYS.CREATE_BRAND}/preview`,
      Icon: MetadataIcon
    }
  ];

  useEffect(() => {
    setRightSection(<RightSidebar list={BrandDetailsNav} />);
    return () => {
      setRightSection(null);
    };
  }, [createBrandForm.preorder, createBrandForm.brand_type]);

  const workingHours = createBrandForm?.brand_working_hours.map((item: any) => {
    return {
      name: item.name.toLowerCase(),
      opening_time: formatTimeToApiFormat(item.opening_time),
      closing_time: formatTimeToApiFormat(item.closing_time),
      active: item.active
    };
  });

  const {
    name,
    description,
    pop_up,
    short_text,
    new: isNew,
    summary,
    logo,
    images,
    brand_business_metadatas,
    brand_keywords,
    brand_descriptive_metadatas,
    coming_soon,
    coming_soon_text,
    preorder,
    is_private,
    preorder_duration_in_hours,
    brand_type,
    cutoff_time,
    brand_partnership_id,
    all_company_available,
    company_ids,
    top,
    slug
  } = createBrandForm;
  const payload = {
    is_private: is_private ?? false,
    cokitchen_id: activeCokitchen.id,
    item_type: category.toUpperCase(),
    brand_working_hours: workingHours,
    name: name,
    short_text: short_text,
    description: description,
    new: isNew,
    top,
    slug,
    pop_up: pop_up,
    preorder_duration_in_hours,
    preorder: preorder ?? false,
    summary,
    brand_partnership_id,
    brand_type,
    cutoff_time: cutoff_time ? `${cutoff_time?.timeWithColon}:00Z` : null,
    all_company_available,
    ...(logo?.trim() !== '' && { logo }),
    ...(images.length && { images }),
    ...(brand_business_metadatas.length && { brand_business_metadatas }),
    ...(brand_descriptive_metadatas.length && { brand_descriptive_metadatas }),
    ...(brand_keywords.length && { brand_keywords }),
    coming_soon,
    ...(coming_soon_text?.trim() !== '' && { coming_soon_text }),
    ...(isFCBBrand(brand_type) && company_ids.length ? { company_ids } : {}),
    excluded_polygon_ids: createBrandForm.polygon.filter((p) => !p.available).map((p) => p.id)
  };
  const handleConfirmForm = async () => {
    setLoading(true);
    try {
      const res = await apiService.post('marketing/brand-new', {
        data: payload
      });
      setNewBrand(res.data.data);
      setShowSuccess(true);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (newBrand.id) {
      switchToLive();
    }
  }, [isActive]);

  const switchToLive = async () => {
    try {
      const payload = {
        brand_id: newBrand.id,
        live: isActive
      };
      const res = await apiService.post('marketing/brand-go-live', {
        data: payload
      });
      if (res.status === 201) {
        toast(
          <ToastMessage type='success' message={`Brand is ${isActive ? 'now live' : 'offline'} `} />
        );
        setShowSuccess(false);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  return (
    <Fragment>
      <PageHeaderText title='Create new brand' description='Ensure you provide correct details' />
      <Outlet />
      <BottomNavigation
        loading={loading}
        list={BrandDetailsNav}
        onConfirm={handleConfirmForm}
        disabled={disabled}
      />
      <SuccessModal onClose={() => setShowSuccess(false)} show={showSuccess}>
        <div>
          <p> Brand Created Successfully</p>
          <div className='flex justify-between border rounded-xl border-[#EAEBEB] px-4 py-3 mt-4'>
            <p className='font-medium'>Take {newBrand.name} live </p>
            <Switch active={isActive} onChange={setIsActive} />
          </div>
        </div>
      </SuccessModal>
    </Fragment>
  );
};

export default CreateBrandContent;
